import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from "../icons/Icon.js";
import "./BearbeitenLeiste.css";
const ZEITPUNKT = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});
/**
 * `seit` kommt von der API als ISO-Zeitstempel (siehe useRecordLock/Aufgabe 13: die 423
 * trägt `lockedBy` und `since` als eigene Angaben, nicht als fertigen Satz). Hier wird der
 * Zeitpunkt selbst formatiert, statt aus einem Text geparst zu werden.
 */
function formatSeit(iso) {
    const zeitpunkt = new Date(iso);
    if (Number.isNaN(zeitpunkt.getTime()))
        return iso;
    return ZEITPUNKT.format(zeitpunkt);
}
/**
 * Die Leiste aus Bearbeiten/Speichern/Verwerfen samt Hinweisband. Zwei Zustände treiben
 * ihre Gestalt: `modus` (wer gerade tut was) und `sperre` (wer sonst noch am Datensatz
 * sitzt) — beide kommen von außen, der Baustein hält selbst nichts.
 *
 * FREMD blendet "Bearbeiten" aus (ein fremd gesperrter Datensatz lässt sich nicht öffnen)
 * und zeigt stattdessen ein Hinweisband mit Halter und Zeitpunkt; "Übernehmen" steht dort
 * nur, wenn `darfUebernehmen` es erlaubt. Im Band steht ausserdem "Nicht mehr warten" —
 * der Rückzug aus dem Warten (2a-Kleinpunkt W1).
 *
 * FEHLER ist seit Aufgabe 13 keine Sackgasse mehr — ein erfolgreicher Herzschlag führt aus
 * useRecordLock zurück nach GEHALTEN. Während der Bearbeitung wird ein fehlgeschlagener
 * Herzschlag deshalb nur als vorübergehende Störung angezeigt: Speichern und Verwerfen
 * bleiben bedienbar, nur ein Hinweisband macht auf die Störung aufmerksam.
 */
export function BearbeitenLeiste(p) {
    const { modus, sperre, darfUebernehmen } = p;
    const fremdGesperrt = sperre.art === "FREMD";
    const speichertGerade = modus === "SPEICHERT";
    const bearbeitetGerade = modus !== "LESEN";
    // Die drei Knöpfe einzeln entschieden — und die Leiste zeichnet ihre Aktionszeile nur,
    // wenn wenigstens einer davon darin steht. Eine leere Zeile wäre keine Zierde, sondern
    // ein zusätzlicher Abstand (die Leiste ist ein Flex-Stapel mit `gap`).
    const zeigtBearbeiten = modus === "LESEN" && !fremdGesperrt;
    const zeigtUebernehmen = fremdGesperrt && darfUebernehmen;
    const zeigtSchreibknoepfe = bearbeitetGerade && p.aktionenImPanel !== true;
    /*
     * „Nicht mehr warten" gibt es NUR ausserhalb eines Panels (Fixrunde 1 zur Modal-Hausregel,
     * 07.09.2026).
     *
     * Der Knopf ist der Rückzug aus dem WARTEN — und Warten ist ein Lesezustand: Es steht
     * nichts Getipptes im Weg, deshalb räumt er ohne Rückfrage ab. Im Panel stimmt diese
     * Voraussetzung nicht mehr. Dort kann eine gehaltene Sperre in FREMD kippen (eine
     * Leitungsfunktion übernimmt sie), und dann stünde das Band mitsamt diesem Knopf ÜBER
     * einem ausgefüllten Formular: ein vierter Ausgang, der die Eingabe wortlos verwirft —
     * genau der Fehler, gegen den die Hausregel geschrieben ist.
     *
     * Verloren geht dabei nichts: „Abbrechen" in der Fußleiste tut dasselbe (es räumt den
     * Bearbeitungswunsch ab und gibt die Sperre frei), nur mit Rückfrage. „Übernehmen" bleibt
     * stehen — es ist kein Ausgang, sondern der Weg weiterzuarbeiten.
     */
    const zeigtRueckzug = fremdGesperrt && p.aktionenImPanel !== true;
    return (_jsxs("div", { className: "ki-leiste", children: [fremdGesperrt && (_jsxs("div", { className: "ki-leiste__hinweis", role: "status", children: [_jsx(Icon, { name: "lock", size: 14 }), _jsxs("span", { children: [sperre.von, " bearbeitet diesen Datensatz seit ", formatSeit(sperre.seit), " Uhr."] }), zeigtRueckzug && (_jsx("button", { type: "button", className: "ki-leiste__hinweis-knopf", onClick: p.onNichtMehrWarten, children: "Nicht mehr warten" }))] })), sperre.art === "FEHLER" && bearbeitetGerade && (_jsxs("div", { className: "ki-leiste__hinweis", role: "status", children: [_jsx(Icon, { name: "triangle-exclamation", size: 14 }), _jsxs("span", { children: ["Die Sperre konnte gerade nicht best\u00E4tigt werden: ", sperre.meldung] })] })), (zeigtBearbeiten || zeigtUebernehmen || zeigtSchreibknoepfe) && (_jsxs("div", { className: "ki-leiste__aktionen", children: [zeigtBearbeiten && (_jsx("button", { type: "button", className: "ki-leiste__primaer", onClick: p.onBearbeiten, children: "Bearbeiten" })), zeigtUebernehmen && (_jsx("button", { type: "button", className: "ki-leiste__primaer", onClick: p.onUebernehmen, children: "\u00DCbernehmen" })), zeigtSchreibknoepfe && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "ki-leiste__primaer", onClick: p.onSpeichern, disabled: speichertGerade, children: "Speichern" }), _jsx("button", { type: "button", className: "ki-leiste__sekundaer", onClick: p.onVerwerfen, disabled: speichertGerade, children: "Verwerfen" })] }))] }))] }));
}
//# sourceMappingURL=BearbeitenLeiste.js.map